import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const EMPTY = { meta: null, teams: [], agents: [], workflows: [], task_groups: [], next_steps: [] };
const CatalogueContext = createContext(null);
const cacheKey = (uid, role) => `fcg-catalogue-${uid}-${role}`;
const readCache = (key) => {
  try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch { return null; }
};

export function CatalogueProvider({ children }) {
  const { user, viewAs } = useAuth();
  const { mergeColours } = useSettings();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const userId = user ? user.id : null;
  const role = viewAs || user?.role;
  const key = userId ? cacheKey(userId, role) : null;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/catalogue", { params: viewAs ? { view_as: viewAs } : {} });
      setState({ loading: false, error: null, data });
      mergeColours(data.settings);
      if (key) sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      setState((s) => ({ loading: false, error: errorMessage(e), data: s.data }));
    }
  }, [key, viewAs, mergeColours]);

  const [favourites, setFavourites] = useState([]);
  useEffect(() => {
    if (!userId) return setState({ loading: true, error: null, data: null });
    const hit = readCache(key);
    setState(hit ? { loading: false, error: null, data: hit } : { loading: true, error: null, data: null });
    load();
    api.get("/favourites").then(({ data }) => setFavourites(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  const toggleFavourite = useCallback(async (targetId) => {
    const { data } = await api.post("/favourites", { target_id: targetId });
    setFavourites((f) => (data.favourite ? [targetId, ...f] : f.filter((x) => x !== targetId)));
    return data.favourite;
  }, []);

  const value = useMemo(() => {
    const d = state.data || EMPTY;
    return {
      ...d,
      loading: state.loading,
      error: state.error,
      reload: load,
      favourites,
      toggleFavourite,
      agentsByKey: Object.fromEntries(d.agents.map((a) => [a.emp_no, a])),
      workflowsByKey: Object.fromEntries(d.workflows.map((w) => [w.id, w])),
      teamsById: Object.fromEntries(d.teams.map((t, i) => [t.id, { ...t, _index: i }])),
      isEmpty: !state.loading && !!state.data && d.agents.length === 0 && d.workflows.length === 0,
    };
  }, [state, load, favourites, toggleFavourite]);

  return <CatalogueContext.Provider value={value}>{children}</CatalogueContext.Provider>;
}

export const useCatalogue = () => useContext(CatalogueContext);
